import { faCheck } from '@hg-akademie-ban/ban-elevate-assets/icons/regular';
import { CheckCircleOutlined, HelpOutlineOutlined, HighlightOffOutlined } from '@mui/icons-material';
import { Typography } from '@mui/material';

import { Button } from '@/components/ui/button.tsx';
import { Tag } from '@/components/ui/tag';

import { type ExperteAntwort, experteAntworten, type Leistung } from '../../../dtos';
import { formatDateMultiRange, getExperteName } from '../../../utils';
import { type TeilleistungColumn } from '../../../utils/teilleistung';
import { TagRadioGroup } from '../TagRadioGroup';
import { LeistungIndexTag } from '../ThemaHeader/LeistungIndexTag';
import { content } from './AusschreibungRueckmeldungenCard.content';
import {
  type AntwortCell,
  getAntwortCell,
  hasPruefenOderAendernForTeilleistung,
} from './AusschreibungRueckmeldungenCard.utils';

type Props = {
  readonly leistung: Leistung | undefined;
  readonly teilleistungColumns?: readonly TeilleistungColumn[];
  readonly teilleistungId?: string;
  readonly showLeistungTag?: boolean;
  readonly date?: string;
  readonly vorschlagSelection?: {
    readonly activeTeilleistungId: string;
    readonly selectedVorschlagKey: string | undefined;
    readonly onVorschlagChange: (vorschlagKey: string) => void;
  };
  readonly onVorschlaegePruefen?: (teilleistungId: string) => void;
};

const cardClassName = 'h-full rounded-sm border border-default bg-subtle p-16';

export const AusschreibungRueckmeldungenCard = ({
  leistung,
  teilleistungColumns = [],
  teilleistungId,
  showLeistungTag = false,
  date,
  vorschlagSelection,
  onVorschlaegePruefen,
}: Props) => {
  if (!leistung) {
    return (
      <div className={cardClassName}>
        <div className="flex flex-col gap-16">
          <Typography fontSize="14px" fontWeight="500">
            {content.titel}
          </Typography>
          <div>{content.leer}</div>
        </div>
      </div>
    );
  }

  const ausschreibung = leistung.ausschreibung;
  const experteAntworten = ausschreibung?.experteAntworten ?? [];
  const pruefenCallback =
    onVorschlaegePruefen &&
    (teilleistungColumns.some((spalte) =>
      hasPruefenOderAendernForTeilleistung(experteAntworten, spalte.teilleistungId),
    ) ||
      (teilleistungId !== undefined && hasPruefenOderAendernForTeilleistung(experteAntworten, teilleistungId)))
      ? onVorschlaegePruefen
      : undefined;
  const renderModulMatrix = teilleistungColumns.length > 0 && ausschreibung !== null;

  return (
    <div className={cardClassName}>
      <div className="flex flex-wrap items-center gap-12">
        {showLeistungTag && <LeistungIndexTag index={leistung.index} />}
        <Typography fontSize="14px" fontWeight="500">
          {content.titel}
        </Typography>
        {date !== undefined && (
          <Typography variant="labelLight" color="textSecondary">
            {date}
          </Typography>
        )}
      </div>
      <div className="mt-20">
        {renderModulMatrix ? (
          <ModulMatrix
            leistung={leistung}
            teilleistungColumns={teilleistungColumns}
            vorschlagSelection={vorschlagSelection}
            onVorschlaegePruefen={pruefenCallback}
          />
        ) : (
          <ExperteAntwortListe
            experteAntworten={ausschreibung?.experteAntworten ?? []}
            teilleistungId={teilleistungId}
            vorschlagSelection={vorschlagSelection}
            onVorschlaegePruefen={pruefenCallback}
          />
        )}
      </div>
    </div>
  );
};

const ModulMatrix = ({
  leistung,
  teilleistungColumns,
  vorschlagSelection,
  onVorschlaegePruefen,
}: {
  readonly leistung: Leistung;
  readonly teilleistungColumns: readonly TeilleistungColumn[];
  readonly vorschlagSelection: Props['vorschlagSelection'];
  readonly onVorschlaegePruefen: Props['onVorschlaegePruefen'];
}) => {
  const experteAntworten = leistung.ausschreibung?.experteAntworten ?? [];
  const zeilen = experteAntworten.length + (onVorschlaegePruefen ? 2 : 1);

  return (
    <div
      className="grid items-start gap-x-24 gap-y-12"
      style={{
        gridTemplateColumns: `220px repeat(${String(teilleistungColumns.length)}, 1fr)`,
        gridTemplateRows: `repeat(${String(zeilen)}, auto)`,
      }}
    >
      {experteAntworten.map((experteAntwort, index) => (
        <Typography
          key={experteAntwort.experte.experteSapId}
          variant="labelLight"
          color="textPrimary"
          sx={{ minWidth: 0, lineHeight: '24px' }}
          style={{ gridColumn: 1, gridRow: index + 2 }}
        >
          {getExperteName(experteAntwort.experte)}
        </Typography>
      ))}
      {teilleistungColumns.map((spalte, index) => (
        <TeilleistungSpalte
          key={spalte.teilleistungId}
          spalte={spalte}
          experteAntworten={experteAntworten}
          column={index + 2}
          vorschlagSelection={
            vorschlagSelection?.activeTeilleistungId === spalte.teilleistungId ? vorschlagSelection : undefined
          }
          onVorschlaegePruefen={onVorschlaegePruefen}
        />
      ))}
    </div>
  );
};

const VorschlaegePruefenButton = ({
  teilleistungId,
  hasSelectedVorschlag = false,
  onVorschlaegePruefen,
}: {
  readonly teilleistungId: string | undefined;
  readonly hasSelectedVorschlag?: boolean;
  readonly onVorschlaegePruefen: Props['onVorschlaegePruefen'];
}) => {
  if (!onVorschlaegePruefen || teilleistungId === undefined) {
    return null;
  }

  return (
    <Button
      label={hasSelectedVorschlag ? content.vorschlagAendern : content.vorschlaegePruefen}
      size="compact"
      className="self-start"
      variant={hasSelectedVorschlag ? 'tertiary' : 'primary'}
      onClick={() => onVorschlaegePruefen(teilleistungId)}
    />
  );
};

const TeilleistungSpalte = ({
  spalte,
  experteAntworten,
  column,
  vorschlagSelection,
  onVorschlaegePruefen,
}: {
  readonly spalte: TeilleistungColumn;
  readonly experteAntworten: readonly ExperteAntwort[];
  readonly column: number;
  readonly vorschlagSelection?: CellVorschlagSelection;
  readonly onVorschlaegePruefen: Props['onVorschlaegePruefen'];
}) => (
  <div
    className="grid items-start justify-self-start self-stretch"
    style={{ gridColumn: column, gridRow: '1 / -1', gridTemplateRows: 'subgrid' }}
  >
    {vorschlagSelection && (
      <div
        className="bg-brand-subtle border-brand -m-8 self-stretch rounded-xs border"
        style={{ gridColumn: 1, gridRow: '1 / -1' }}
      />
    )}
    <Typography variant="label" color="textPrimary" style={{ gridColumn: 1, gridRow: 1 }}>
      {spalte.label}
      {spalte.date !== undefined && (
        <Typography component="span" variant="labelLight" color="textSecondary" sx={{ ml: 1 }}>
          {spalte.date}
        </Typography>
      )}
    </Typography>
    {experteAntworten.map((experteAntwort, index) => (
      <div key={experteAntwort.experte.experteSapId} style={{ gridColumn: 1, gridRow: index + 2 }}>
        <AntwortCellView
          cell={getAntwortCell(experteAntwort, spalte.teilleistungId)}
          vorschlagSelection={vorschlagSelection}
        />
      </div>
    ))}
    {onVorschlaegePruefen && (
      <div style={{ gridColumn: 1, gridRow: experteAntworten.length + 2 }}>
        <VorschlaegePruefenButton
          teilleistungId={
            hasPruefenOderAendernForTeilleistung(experteAntworten, spalte.teilleistungId)
              ? spalte.teilleistungId
              : undefined
          }
          hasSelectedVorschlag={experteAntworten.some((antwort) =>
            antwort.alternativvorschlaege.some(
              (vorschlag) => vorschlag.teilleistungId === spalte.teilleistungId && vorschlag.isSelected,
            ),
          )}
          onVorschlaegePruefen={onVorschlaegePruefen}
        />
      </div>
    )}
  </div>
);

type CellVorschlagSelection = NonNullable<Props['vorschlagSelection']>;

const AntwortCellView = ({
  cell,
  vorschlagSelection,
}: {
  readonly cell: AntwortCell;
  readonly vorschlagSelection?: CellVorschlagSelection;
}) => {
  switch (cell.art) {
    case 'angenommen':
      return <AntwortIcon art="angenommen" />;
    case 'abgelehnt':
      return <AntwortIcon art="abgelehnt" />;
    case 'abgelaufenAusschreibung':
      return <AntwortIcon art="abgelaufenAusschreibung" />;
    case 'abgelaufenAngebot':
      return <AntwortIcon art="abgelaufenAngebot" />;
    case 'vorbehalt':
      return (
        <div className="flex items-start gap-8">
          <AntwortIcon art="vorbehalt" />
          <AlternativvorschlagChips vorschlaege={cell.vorschlaege} vorschlagSelection={vorschlagSelection} />
        </div>
      );
    case 'keineAntwort':
      return <div>{content.leer}</div>;
  }
};

const ExperteAntwortListe = ({
  experteAntworten: antworten,
  teilleistungId,
  vorschlagSelection,
  onVorschlaegePruefen,
}: {
  readonly experteAntworten: readonly ExperteAntwort[];
  readonly teilleistungId: Props['teilleistungId'];
  readonly vorschlagSelection: Props['vorschlagSelection'];
  readonly onVorschlaegePruefen: Props['onVorschlaegePruefen'];
}) => {
  const buttonZeile = antworten.length + 1;

  return (
    <div
      className="grid items-start gap-x-12 gap-y-24"
      style={{
        gridTemplateColumns: '220px 24px max-content',
        gridTemplateRows: `repeat(${String(Math.max(onVorschlaegePruefen ? buttonZeile : antworten.length, 1))}, auto)`,
      }}
    >
      {vorschlagSelection && (
        <div
          className="bg-brand-subtle border-brand -m-8 self-stretch rounded-xs border"
          style={{ gridColumn: '2 / 4', gridRow: '1 / -1' }}
        />
      )}
      {antworten.map((experteAntwort, index) => (
        <ExperteAntwortRow
          key={experteAntwort.experte.experteSapId}
          experteAntwort={experteAntwort}
          vorschlagSelection={vorschlagSelection}
          row={index + 1}
        />
      ))}
      <div className="flex justify-start" style={{ gridColumn: '2 / 4', gridRow: buttonZeile }}>
        <VorschlaegePruefenButton
          teilleistungId={
            teilleistungId !== undefined && hasPruefenOderAendernForTeilleistung(antworten, teilleistungId)
              ? teilleistungId
              : undefined
          }
          hasSelectedVorschlag={antworten.some((antwort) =>
            antwort.alternativvorschlaege.some(
              (vorschlag) => vorschlag.teilleistungId === teilleistungId && vorschlag.isSelected,
            ),
          )}
          onVorschlaegePruefen={onVorschlaegePruefen}
        />
      </div>
    </div>
  );
};

const ExperteAntwortRow = ({
  experteAntwort,
  vorschlagSelection,
  row,
}: {
  readonly experteAntwort: ExperteAntwort;
  readonly vorschlagSelection: Props['vorschlagSelection'];
  readonly row: number;
}) => (
  <>
    <Typography
      variant="labelLight"
      color="textPrimary"
      sx={{ minWidth: 0, lineHeight: '24px' }}
      style={{ gridColumn: 1, gridRow: row }}
    >
      {getExperteName(experteAntwort.experte)}
    </Typography>
    <div style={{ gridColumn: 2, gridRow: row }}>
      <RowAntwortIcon experteAntwort={experteAntwort} />
    </div>
    {experteAntwort.alternativvorschlaege.length > 0 && (
      <div style={{ gridColumn: 3, gridRow: row }}>
        <AlternativvorschlagChips
          vorschlaege={experteAntwort.alternativvorschlaege}
          vorschlagSelection={vorschlagSelection}
        />
      </div>
    )}
  </>
);

const AlternativvorschlagChips = ({
  vorschlaege,
  vorschlagSelection,
}: {
  readonly vorschlaege: ExperteAntwort['alternativvorschlaege'];
  readonly vorschlagSelection?: CellVorschlagSelection;
}) => {
  const sichtbareVorschlaege = vorschlaege.filter(
    (alternativvorschlag) => alternativvorschlag.leistungszeitraeume.length > 0,
  );

  if (vorschlagSelection) {
    return (
      <TagRadioGroup
        value={vorschlagSelection.selectedVorschlagKey}
        onValueChange={vorschlagSelection.onVorschlagChange}
        options={sichtbareVorschlaege.map((alternativvorschlag) => ({
          value: String(alternativvorschlag.id),
          label: formatDateMultiRange(alternativvorschlag.leistungszeitraeume),
        }))}
      />
    );
  }

  return (
    <div className="flex flex-col items-start gap-4">
      {sichtbareVorschlaege.map((alternativvorschlag) => (
        <Tag
          key={alternativvorschlag.id}
          variant="outline"
          size="compact"
          label={formatDateMultiRange(alternativvorschlag.leistungszeitraeume)}
          icon={alternativvorschlag.isSelected ? faCheck : undefined}
          aria-label={alternativvorschlag.isSelected ? content.vorschlagUebernommen : undefined}
        />
      ))}
    </div>
  );
};

type IconArt = 'angenommen' | 'abgelehnt' | 'abgelaufenAusschreibung' | 'abgelaufenAngebot' | 'vorbehalt';

const AntwortIcon = ({ art }: { readonly art: IconArt }) => {
  switch (art) {
    case 'angenommen':
      return <CheckCircleOutlined aria-label={content.antwortAngenommen} color="success" />;
    case 'vorbehalt':
      return <HelpOutlineOutlined aria-label={content.antwortUnterVorbehaltAngenommen} color="action" />;
    case 'abgelehnt':
      return <HighlightOffOutlined aria-label={content.antwortAbgelehnt} color="error" />;
    case 'abgelaufenAusschreibung':
      return <HighlightOffOutlined aria-label={content.ausschreibungAbgelaufen} color="error" />;
    case 'abgelaufenAngebot':
      return <HighlightOffOutlined aria-label={content.angebotAbgelaufen} color="error" />;
  }
};

const RowAntwortIcon = ({ experteAntwort }: { readonly experteAntwort: ExperteAntwort }) => {
  switch (experteAntwort.antwort) {
    case experteAntworten.AUSSCHREIBUNG_ANGENOMMEN:
    case experteAntworten.ANGEBOT_ANGENOMMEN:
      return <AntwortIcon art="angenommen" />;
    case experteAntworten.AUSSCHREIBUNG_UNTER_VORBEHALT_ANGENOMMEN:
      return <AntwortIcon art="vorbehalt" />;
    case experteAntworten.AUSSCHREIBUNG_ABGELEHNT:
    case experteAntworten.ANGEBOT_ABGELEHNT:
      return <AntwortIcon art="abgelehnt" />;
    case experteAntworten.AUSSCHREIBUNG_ABGELAUFEN:
      return <AntwortIcon art="abgelaufenAusschreibung" />;
    case experteAntworten.ANGEBOT_ABGELAUFEN:
      return <AntwortIcon art="abgelaufenAngebot" />;
  }
};
